// Shared validation utilities
import DOMPurify from "dompurify";
import { decodeHTML } from "./decodeHTML";

/**
 * Validate a collection name for length and forbidden characters.
 * Rules:
 * - Length between 3 and 250 characters
 * - Disallow "/" and ASCII control chars (0–31) and DEL (127)
 * - Disallow characters commonly breaking tooling: <>#%{}|\^~[]`
 *
 * @param {string} value
 * @returns {string|null} error message or null if valid
 */
export const validateCollectionName = (value) => {
  const textErrorLength =
    "Value error: Name must be between 3 and 250 characters long.";
  if (!value) {
    return textErrorLength;
  }
  if (value.length < 3 || value.length > 250) {
    return textErrorLength;
  }
  if (value.includes("/")) {
    return "Invalid name. Character not allowed: /";
  }

  // Check for control characters without using control-char regex (eslint no-control-regex)
  for (const ch of value) {
    const code = ch.codePointAt(0);
    if (code !== undefined && (code < 32 || code === 127)) {
      return "Control characters are not allowed in collection names.";
    }
  }

  // Check other disallowed visible characters via regex
  const unsafeCharacters = /[<>#%{}|\\^~[\]`]/g;
  const foundUnsafeChars = value.match(unsafeCharacters);

  if (foundUnsafeChars) {
    return `Invalid name. Character not allowed: ${[
      ...new Set(foundUnsafeChars),
    ].join(", ")}`;
  }
  return null;
};

const stripHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

/**
 * Validate collection description content after sanitization.
 * Rules:
 * - Max length 2600, min length 3 if not empty
 * - Allowed characters: [\w\s!@#$%&*()_\-+=.,'":;?/]
 *
 * @param {string} value
 * @returns {string|null}
 */
export const validateCollectionDescription = (value) => {
  const cleanText = stripHtml(DOMPurify.sanitize(decodeHTML(value)));

  if (cleanText.length > 2600) {
    return "Input must be less than 2600 characters";
  }
  if (cleanText.length > 0 && cleanText.length < 3) {
    return "Input must be at least 3 characters";
  }

  const regex = /^[\w\s!@#$%&*()_\-+=.,'":;?]*$/;
  if (!regex.test(cleanText)) {
    return "Input contains invalid characters. Allowed characters: ! @ # $ % & * ( ) _ - + = . , ' \" : ; ? /";
  }
  return null;
};
